<template>
  <cui-filter-wrapper :render-clear-button="false" class="mt-2 filter-wrapper">
    <!--    Devices filters section-->
    <div v-if="isModelForContext(model, FilterContext.MSP_DEVICES)" class="d-flex flex-column">
      <msp-workspace-descentant-select
        class="mr-2 filter"
        :value="model.childWorkspaceIds"
        :disabled="disableFilters"
        @value-updated="model.childWorkspaceIds = $event"
      />
      <export-devices-filters
        :model-value="model"
        :labels="devicesStore.availableLabels"
        :statuses="devicesStore.statuses"
        :raw-os-versions="osVersionsFromStore"
        :raw-client-versions="clientVersionsFromStore"
        :disabled="disableFilters"
        @update:model-value="Object.assign(model, $event)"
      />
    </div>

    <!--    Users filters section-->
    <div v-else-if="isModelForContext(model, FilterContext.MSP_USERS)" class="d-flex flex-column">
      <msp-workspace-descentant-select
        class="mb-2 filter"
        :value="model.workspaceIds as string[]"
        :disabled="disableFilters"
        @value-updated="model.workspaceIds = $event"
      />
      <users-filters
        :model-value="model"
        :services="services"
        :disabled="disableFilters"
        is-vertical
        @update:model-value="Object.assign(model, $event)"
      />
    </div>

    <!--    Tickets Filters section-->
    <div v-else-if="isModelForContext(model, FilterContext.MSP_TICKETS)" class="d-flex flex-column">
      <msp-workspace-descentant-select
        class="mr-2 filter"
        :value="model.childWorkspaceIds as string[]"
        :disabled="disableFilters"
        @value-updated="model.childWorkspaceIds = $event"
      />
      <tickets-filters
        :model-value="model"
        :disabled="disableFilters"
        :teleport-date-picker="true"
        is-vertical
        @update:model-value="Object.assign(model, $event)"
      />
    </div>
    <!--    MSP workspace filters section — uses shared atomic filter components -->
    <div v-else-if="isModelForContext(model, FilterContext.MSP)" class="d-flex flex-column">
      <msp-plan-filter
        v-model:subscription-types="model.subscriptionTypes"
        v-model:near-expiration-only="model.nearExpirationOnly"
        :plans="plans"
        :disabled="disableFilters"
        class="filter-menu mb-2"
      />
      <msp-labels-filter
        v-model="model.labels"
        :items="workspaceLabels"
        :disabled="disableFilters"
        class="filter-menu mb-2"
      />
      <msp-policy-filter
        v-model="model.mspPolicyId"
        :items="globalPolicies"
        :disabled="disableFilters"
        class="filter-menu mb-2"
      />
      <msp-addons-filter
        v-model="model.addons"
        :disabled="disableFilters"
        class="filter-menu mb-2"
      />
    </div>
  </cui-filter-wrapper>
</template>

<script lang="ts" generic="T">
import { defineComponent, onMounted, type PropType, type Ref, ref, watch } from "vue";
import { defaultFiltersState, FilterContext, type FiltersState } from "@/_store/filters.module";
import { CuiFilterWrapper } from "@coronet/cui";
import { storeToRefs } from "pinia";
import { useI18n } from "vue-i18n";
import cloneDeep from "lodash/cloneDeep";
import { useMspDevicesStore } from "@/_store/msp/devices/msp-devices.module.ts";
import MspWorkspaceDescentantSelect from "@/components/msp/MspWorkspaceDescentantSelect.vue";
import { Service } from "@/constants/cloud-apps.ts";
import { SubscriptionType } from "@/constants/workplaces.ts";
import { useMspLabelsStore } from "@/_store/msp/msp-labels.module.ts";
import {
  type GlobalPolicyListItem,
  useMspGlobalPoliciesStore,
} from "@/_store/msp/global-policies/msp-global-policies.module.ts";
import MspAddonsFilter from "@/components/msp/filters/MspAddonsFilter.vue";
import MspPlanFilter from "@/components/msp/filters/MspPlanFilter.vue";
import MspLabelsFilter from "@/components/msp/filters/MspLabelsFilter.vue";
import MspPolicyFilter from "@/components/msp/filters/MspPolicyFilter.vue";
import ExportDevicesFilters from "@/components/devices/ExportDevicesFilters.vue";
import UsersFilters from "@/components/users/UsersFilters.vue";
import TicketsFilters from "@/components/tickets/TicketsFilters.vue";

type FiltersType =
  | FilterContext.MSP_TICKETS
  | FilterContext.MSP_DEVICES
  | FilterContext.MSP_USERS
  | FilterContext.MSP;

export default defineComponent({
  components: {
    MspWorkspaceDescentantSelect,
    CuiFilterWrapper,
    ExportDevicesFilters,
    UsersFilters,
    TicketsFilters,
    MspAddonsFilter,
    MspPlanFilter,
    MspLabelsFilter,
    MspPolicyFilter,
  },
  props: {
    filterContext: {
      type: String as PropType<FiltersType>,
      required: true,
    },
    disableFilters: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  emits: ["update:filters"],
  setup(props, { emit }) {
    const i18n = useI18n();
    const model: Ref<FiltersState[typeof props.filterContext]> = ref(
      cloneDeep(defaultFiltersState[props.filterContext])
    );
    const devicesStore = useMspDevicesStore();
    const labelsStore = useMspLabelsStore();
    const globalPolicyStore = useMspGlobalPoliciesStore();
    const globalPolicies: Ref<GlobalPolicyListItem[]> = ref([]);
    const { labels: workspaceLabels } = storeToRefs(labelsStore);
    const { osVersions: osVersionsFromStore, clientVersions: clientVersionsFromStore } =
      storeToRefs(devicesStore);

    const getGlobalPolicies = async (search: string = "") => {
      try {
        const { data } = await globalPolicyStore.searchGlobalPolicies(search);
        // We need to have none option in policies filter, that will display workspaces
        // without policies, and here's the hack to achieve that. Adding fake policy
        const fakeNonePolicy = {
          modifiedAt: 0,
          name: i18n.t("general.none"),
          assignedWorkspaceCount: 0,
          description: "",
          sections: [],
          id: "NONE",
        };
        globalPolicies.value = [fakeNonePolicy, ...data.items];
      } catch (e) {
        console.error(e);
      }
    };

    onMounted(async () => {
      await devicesStore.init();
      await labelsStore.getLabels(false);
      await getGlobalPolicies();
    });

    watch(
      () => model.value,
      (newVal) => {
        emit("update:filters", newVal);
      },
      { deep: true, immediate: true }
    );

    const isModelForContext = <T extends FiltersType>(
      filterModel: typeof model.value,
      filterContext: T
    ): filterModel is FiltersState[T] => {
      return props.filterContext === filterContext;
    };

    return {
      FilterContext,
      model,
      devicesStore,
      osVersionsFromStore,
      clientVersionsFromStore,
      isModelForContext,
      globalPolicies,
      workspaceLabels,
      plans: [
        SubscriptionType.INITIAL,
        SubscriptionType.SUBSCRIPTION,
        SubscriptionType.FREEZE,
        SubscriptionType.TRIAL,
      ],
      services: [
        Service.BOX,
        Service.DROPBOX,
        Service.G_SUITE,
        Service.OFFICE_365,
        Service.SLACK,
        Service.SALES_FORCE,
      ],
    };
  },
});
</script>

<style lang="scss" scoped>
.filter,
.filter-menu,
.range-picker {
  width: 240px !important;
}
</style>
